/**
 * Crypto Icons System — Supabank Canon
 *
 * SSOT for cryptocurrency icon rendering.
 * Uses cryptocurrency-icons package (CC0 license).
 *
 * @see https://github.com/spothq/cryptocurrency-icons
 */

// Supported cryptocurrencies with metadata
export const CRYPTO_REGISTRY = {
  // Major cryptocurrencies
  btc: { name: 'Bitcoin', symbol: 'BTC', color: '#F7931A' },
  eth: { name: 'Ethereum', symbol: 'ETH', color: '#627EEA' },
  usdt: { name: 'Tether', symbol: 'USDT', color: '#26A17B' },
  usdc: { name: 'USD Coin', symbol: 'USDC', color: '#2775CA' },
  sol: { name: 'Solana', symbol: 'SOL', color: '#9945FF' },
  xrp: { name: 'Ripple', symbol: 'XRP', color: '#23292F' },
  bnb: { name: 'BNB', symbol: 'BNB', color: '#F3BA2F' },
  ada: { name: 'Cardano', symbol: 'ADA', color: '#0033AD' },
  doge: { name: 'Dogecoin', symbol: 'DOGE', color: '#C2A633' },
  matic: { name: 'Polygon', symbol: 'MATIC', color: '#8247E5' },
  dot: { name: 'Polkadot', symbol: 'DOT', color: '#E6007A' },
  avax: { name: 'Avalanche', symbol: 'AVAX', color: '#E84142' },
  link: { name: 'Chainlink', symbol: 'LINK', color: '#2A5ADA' },
  ltc: { name: 'Litecoin', symbol: 'LTC', color: '#345D9D' },
  uni: { name: 'Uniswap', symbol: 'UNI', color: '#FF007A' },

  // Stablecoins
  dai: { name: 'Dai', symbol: 'DAI', color: '#F5AC37' },
  busd: { name: 'Binance USD', symbol: 'BUSD', color: '#F0B90B' },
  tusd: { name: 'TrueUSD', symbol: 'TUSD', color: '#2B2E7F' },
  usdp: { name: 'Pax Dollar', symbol: 'USDP', color: '#085229' },
  frax: { name: 'Frax', symbol: 'FRAX', color: '#000000' },
  // Bridge-supported stablecoins
  usdb: { name: 'USD Bridge', symbol: 'USDB', color: '#0066FF' },
  eurc: { name: 'Euro Coin', symbol: 'EURC', color: '#003399' },
  pyusd: { name: 'PayPal USD', symbol: 'PYUSD', color: '#003087' },

  // Layer 2 & Other
  arb: { name: 'Arbitrum', symbol: 'ARB', color: '#28A0F0' },
  op: { name: 'Optimism', symbol: 'OP', color: '#FF0420' },
  base: { name: 'Base', symbol: 'BASE', color: '#0052FF' },
  near: { name: 'NEAR Protocol', symbol: 'NEAR', color: '#000000' },
  atom: { name: 'Cosmos', symbol: 'ATOM', color: '#2E3148' },
  xlm: { name: 'Stellar', symbol: 'XLM', color: '#000000' },
  algo: { name: 'Algorand', symbol: 'ALGO', color: '#000000' },
  trx: { name: 'TRON', symbol: 'TRX', color: '#FF0013' },
} as const;

export type CryptoSymbol = keyof typeof CRYPTO_REGISTRY;

/**
 * Get SVG path for cryptocurrency icon
 * @param symbol - Lowercase symbol (e.g., 'btc', 'eth')
 * @param style - Icon style: 'color' (default), 'white', 'black', 'icon'
 */
export function getCryptoIconPath(
  symbol: string,
  style: 'color' | 'white' | 'black' | 'icon' = 'color'
): string {
  const normalizedSymbol = symbol.toLowerCase();
  return `/node_modules/cryptocurrency-icons/svg/${style}/${normalizedSymbol}.svg`;
}

/**
 * Get crypto metadata by symbol
 */
export function getCryptoMeta(symbol: string): {
  name: string;
  symbol: string;
  color: string;
} | null {
  const normalizedSymbol = symbol.toLowerCase() as CryptoSymbol;
  return CRYPTO_REGISTRY[normalizedSymbol] || null;
}

/**
 * Check if symbol is a known cryptocurrency
 */
export function isCrypto(symbol: string): boolean {
  return symbol.toLowerCase() in CRYPTO_REGISTRY;
}

/**
 * Get all supported crypto symbols
 */
export function getSupportedCryptos(): CryptoSymbol[] {
  return Object.keys(CRYPTO_REGISTRY) as CryptoSymbol[];
}

/**
 * Stablecoins list for special handling
 */
export const STABLECOINS = ['usdt', 'usdc', 'dai', 'busd', 'tusd', 'usdp', 'frax', 'usdb', 'eurc', 'pyusd'] as const;

export function isStablecoin(symbol: string): boolean {
  return STABLECOINS.includes(symbol.toLowerCase() as typeof STABLECOINS[number]);
}
